package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/*
 * SH-124: the tip split. A manager enters the tips collected in a day or a week (the pool); the
 * pool is shared by the hours each person worked in it (clocked time less the unpaid break,
 * SH-116 BreakRules), optionally weighted per role, in whole grosze that add up to the pool.
 *
 * Not pay: EVoShift does not tax, report or pay tips; the screens say the employer and their
 * accountant decide that. Money is in minor units (grosze) so the split is exact.
 *
 * The server computes and stores the split (functions/tips.js mirrors [TipSplit]; the tests of
 * both use the same cases): workplaces/{wpId}/tip_pools/{start}_{end} for managers and
 * workplaces/{wpId}/tip_shares/{poolId}_{userId}, each person's own share, for that person.
 */

/** How long a pool runs: one day, or the seven days of a week. */
enum class TipPeriod { DAY, WEEK }

/** One person's part of a pool: [minutesWorked] are the hours that counted, unweighted. */
data class TipShare(
    val userId: String,
    val displayName: String,
    /** The role their hours counted at (the role of their last shift in the pool), if any. */
    val role: String?,
    val minutesWorked: Long,
    val amountMinor: Long
)

/** A pool as managers see it (workplaces/{wpId}/tip_pools/{id}). */
data class TipPool(
    val id: String,
    val workplaceId: String,
    val period: TipPeriod,
    /** ISO dates, inclusive. */
    val startDate: String,
    val endDate: String,
    val amountMinor: Long,
    val currency: String,
    /** Role → weight in percent (100 = an hour counts once); roles not listed count at 100. */
    val roleWeights: Map<String, Int> = emptyMap(),
    val shares: List<TipShare> = emptyList(),
    val updatedAt: String? = null,
    val updatedBy: String? = null,
    /** Where it was entered: APP, or KIOSK (the tablet). */
    val source: String? = null
) {
    /** Nobody has clocked hours in the period yet: the shares come as people clock out. */
    val waitingForHours: Boolean get() = shares.isEmpty()
}

/** The signed-in person's own share of one pool (workplaces/{wpId}/tip_shares/{poolId}_{uid}). */
data class MyTipShare(
    val poolId: String,
    val workplaceId: String,
    val period: TipPeriod,
    val startDate: String,
    val endDate: String,
    val amountMinor: Long,
    val currency: String,
    val minutesWorked: Long
)

/** What a manager saves: the server works out the period's end and the shares. */
data class TipPoolDraft(
    val workplaceId: String,
    val period: TipPeriod,
    val startDate: String,
    val amountMinor: Long,
    val roleWeights: Map<String, Int> = emptyMap()
)

/** A day's pool as the tablet shows it to the manager who entered it. */
data class KioskTipPool(
    val date: String,
    val amountMinor: Long,
    val currency: String,
    val shares: List<TipShare>
)

/** One person's hours in a pool, weighted: what the split divides by. */
data class TipPoints(val userId: String, val points: Long)

/** Why a pool cannot be saved (the screen words it). */
class TipException(val reason: Reason) : Exception("Tips: $reason") {
    enum class Reason {
        /** Not a whole number of grosze from 0.01 to [TipSplit.MAX_AMOUNT_MINOR]. */
        INVALID_AMOUNT,
        /** A day, or a week of seven days, with a readable start. */
        INVALID_PERIOD,
        /** Weights are whole percents from 0 to [TipSplit.MAX_WEIGHT]. */
        INVALID_WEIGHT,
        UNKNOWN;

        companion object {
            fun of(name: String?): Reason = entries.find { it.name == name } ?: UNKNOWN
        }
    }
}

/**
 * SH-124: the split, shared by the app's preview and (mirrored in functions/tips.js) the server.
 *
 * - Whose hours: shifts at the workplace dated in the period, assigned and clocked in and out.
 * - Minutes: clocked time less the unpaid break ([BreakRules.workedMinutes]).
 * - Points: minutes × the weight of the shift's role (its required role, else the person's role
 *   at the workplace; [DEFAULT_WEIGHT] when the role has none).
 * - Each person gets floor(pool × points / all points) grosze; the grosze left over (fewer than
 *   the people) go one each to the largest remainders, ties to more points, then by user id. The
 *   shares therefore always add up to the pool. People with no points get nothing and are not
 *   listed; with no points at all nothing is shared yet.
 */
object TipSplit {
    /** 100 000 zł: well above any real day's or week's tips, and safe from overflow. */
    const val MAX_AMOUNT_MINOR = 10_000_000L
    const val DEFAULT_WEIGHT = 100
    const val MAX_WEIGHT = 500
    const val WEEK_DAYS = 7

    /** The pool's document id: `{startDate}_{endDate}`. */
    fun poolId(startDate: String, endDate: String) = "${startDate}_$endDate"

    /** The share's document id: `{poolId}_{userId}`. */
    fun shareId(poolId: String, userId: String) = "${poolId}_$userId"

    /**
     * The period of [period] around [date]: the day itself, or the week that holds it starting on
     * [firstDayOfWeek] (ISO, 1 = Monday … 7 = Sunday).
     */
    fun periodOf(period: TipPeriod, date: LocalDate, firstDayOfWeek: Int = 1): Pair<LocalDate, LocalDate> = when (period) {
        TipPeriod.DAY -> date to date
        TipPeriod.WEEK -> {
            val first = firstDayOfWeek.coerceIn(1, 7)
            val back = (date.dayOfWeek.ordinal + 1 - first + 7) % 7
            val start = date.minus(DatePeriod(days = back))
            start to start.plus(DatePeriod(days = WEEK_DAYS - 1))
        }
    }

    /** The end date of a pool of [period] starting on [startDate]. */
    fun endOf(period: TipPeriod, startDate: LocalDate): LocalDate =
        if (period == TipPeriod.DAY) startDate else startDate.plus(DatePeriod(days = WEEK_DAYS - 1))

    /** Exact shares of [amountMinor] by [points] (see the class comment); people without points are left out. */
    fun split(amountMinor: Long, points: List<TipPoints>): Map<String, Long> {
        val counted = points.filter { it.points > 0 }
        val total = counted.sumOf { it.points }
        if (amountMinor <= 0 || total <= 0) return emptyMap()
        val base = counted.associate { it.userId to amountMinor * it.points / total }
        var left = amountMinor - base.values.sum()
        val order = counted.sortedWith(
            compareByDescending<TipPoints> { amountMinor * it.points % total }
                .thenByDescending { it.points }
                .thenBy { it.userId }
        )
        val shares = base.toMutableMap()
        for (p in order) {
            if (left <= 0) break
            shares[p.userId] = shares.getValue(p.userId) + 1
            left--
        }
        return shares
    }

    /** The weight of [role] in percent. */
    fun weightOf(role: String?, weights: Map<String, Int>): Int =
        role?.let { weights[it] } ?: DEFAULT_WEIGHT

    /**
     * The shares of [amountMinor] for [workplaceId]'s [shifts] dated [startDate]..[endDate]:
     * [people] gives names and roles at the workplace (by user id); unknown people are named by
     * the shift, else by their id.
     */
    fun shares(
        amountMinor: Long,
        shifts: List<Shift>,
        workplaceId: String,
        startDate: String,
        endDate: String,
        policy: BreakPolicy,
        weights: Map<String, Int>,
        people: Map<String, User> = emptyMap(),
        /** SH-134: the payable times (from the planned start where the workplace counts so). */
        clockReview: ClockReviewSettings = ClockReviewSettings()
    ): List<TipShare> {
        data class Worked(val userId: String, val role: String?, val minutes: Long, val points: Long)
        val worked = shifts
            .filter { RosterIds.workplaceIdOf(it.rosterId) == workplaceId && it.date in startDate..endDate }
            .sortedWith(compareBy({ it.date }, { it.plannedTime.start }))
            .mapNotNull { shift ->
                // SH-128/SH-134: who worked it (recorded afterwards), else the assignee.
                val userId = shift.workerId ?: return@mapNotNull null
                val actual = shift.payableTimes(clockReview) ?: return@mapNotNull null
                val start = BreakRules.parse(actual.start) ?: return@mapNotNull null
                val end = BreakRules.parse(actual.end) ?: return@mapNotNull null
                if (end <= start) return@mapNotNull null
                val minutes = BreakRules.workedMinutes(shift.breaks, start, end, policy)
                val role = shift.requiredRole?.takeIf { it.isNotBlank() } ?: people[userId]?.engagements?.get(workplaceId)?.role
                Worked(userId, role, minutes, minutes * weightOf(role, weights))
            }
        val byPerson = worked.groupBy { it.userId }
        val amounts = split(amountMinor, byPerson.map { (uid, list) -> TipPoints(uid, list.sumOf { it.points }) })
        val names = shifts.filter { it.userId != null && !it.userDisplayName.isNullOrBlank() }.associate { it.userId!! to it.userDisplayName!! }
        return byPerson.mapNotNull { (uid, list) ->
            val amount = amounts[uid] ?: return@mapNotNull null
            TipShare(
                userId = uid,
                displayName = people[uid]?.displayName?.takeIf { it.isNotBlank() } ?: names[uid] ?: uid,
                role = list.last().role,
                minutesWorked = list.sumOf { it.minutes },
                amountMinor = amount
            )
        }.sortedWith(compareByDescending<TipShare> { it.amountMinor }.thenBy { it.displayName.lowercase() })
    }

    /**
     * A typed amount ("85", "85,5", "1 234.50 zł") in grosze; null when it is not a positive amount
     * with at most two decimals up to [MAX_AMOUNT_MINOR]. Checked character by character.
     */
    fun parseAmount(text: String): Long? {
        val cleaned = text.trim().removeSuffix("zł").removeSuffix("PLN").trim()
            .filter { it != ' ' && it != ' ' }
        if (cleaned.isEmpty()) return null
        val separators = cleaned.count { it == ',' || it == '.' }
        if (separators > 1 || cleaned.any { !it.isDigit() && it != ',' && it != '.' }) return null
        val parts = cleaned.split(',', '.')
        val whole = parts[0]
        val fraction = parts.getOrNull(1).orEmpty()
        if (fraction.length > 2 || (whole.isEmpty() && fraction.isEmpty()) || whole.length > 9) return null
        val minor = (whole.ifEmpty { "0" }.toLong()) * 100 + fraction.padEnd(2, '0').ifEmpty { "00" }.toLong()
        return minor.takeIf { it in 1..MAX_AMOUNT_MINOR }
    }

    /** Checks a draft before it goes to the server; throws [TipException]. */
    fun validate(draft: TipPoolDraft) {
        if (draft.amountMinor !in 1..MAX_AMOUNT_MINOR) throw TipException(TipException.Reason.INVALID_AMOUNT)
        if (runCatching { LocalDate.parse(draft.startDate) }.isFailure) throw TipException(TipException.Reason.INVALID_PERIOD)
        if (draft.roleWeights.values.any { it !in 0..MAX_WEIGHT } || draft.roleWeights.keys.any { it.isBlank() || it.length > 60 }) {
            throw TipException(TipException.Reason.INVALID_WEIGHT)
        }
    }
}
